import { Button, Checkbox, Classes, FormGroup, InputGroup, Radio, RadioGroup } from "@blueprintjs/core";
import { DatePicker, TimePicker } from "@blueprintjs/datetime";
import { Popover2 } from "@blueprintjs/popover2";
import { TimezonePicker, TimezoneDisplayFormat } from "@blueprintjs/timezone";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import DayOfMonth from "./DayOfMonth";
import DayOfWeek from "./DayOfWeek";
import Periodic from "./Periodic";
import Runtime from "./Runtime";

import "./Schedule.scss";
import { NewScheduleState, ScheduleFrequency } from "types";
import { parseCron, shortDate } from "utils/utils";

export default function Schedule() {
    const [currentTime] = useState(new Date());
    const { values, setFieldValue } = useFormikContext<NewScheduleState>();
    const {
        startDate,
        startTime,
        scheduleTime,
        scheduleType,
        timeZone,
        daysOfWeek,
        daysOfMonth,
        periodic,
        scheduleName,
        active
    } = values;

    const updateValue = useCallback(
        (name: keyof NewScheduleState, value: any) => {
            setFieldValue(name, value);
        },
        [setFieldValue]
    );

    const handleScheduleTimeChange = useCallback(
        (d: Date) => {
            const t = getFormattedTime(d);
            updateValue("scheduleTime", t);
        },
        [updateValue]
    );

    const handleFrequencyChange = (e: React.FormEvent<HTMLInputElement>) => {
        const t = e.currentTarget.value as ScheduleFrequency;
        updateValue("scheduleType", t);
        updateValue("daysOfWeek", []);
        updateValue("daysOfMonth", []);
        updateValue("periodic", {});
    };

    useEffect(() => {
        if (scheduleType === ScheduleFrequency.Periodically) {
            updateValue(
                "cron",
                parseCron(startDate, startTime, ScheduleFrequency.Periodically, undefined, undefined, periodic)
            );
        } else {
            updateValue("cron", parseCron(startDate, scheduleTime, scheduleType, daysOfWeek, daysOfMonth, periodic));
        }
    }, [daysOfMonth, daysOfWeek, periodic, scheduleTime, scheduleType, startDate, startTime, updateValue]);

    return (
        <div className={Classes.DIALOG_BODY}>
            <Runtime update={updateValue} />
            <FormGroup key={`schedulename`} label={"Schedule Name"} labelFor="text-input" labelInfo={"*"}>
                <InputGroup
                    key={`schedulename_input`}
                    value={scheduleName}
                    onChange={(e) => updateValue("scheduleName", e.target.value)}
                />
                <Checkbox
                    key={`active`}
                    inline
                    alignIndicator="left"
                    label={"Active"}
                    checked={active}
                    onChange={() => updateValue("active", !active)}
                />
            </FormGroup>
            <div style={{ display: "flex", gap: "2rem" }}>
                <FormGroup key={`startdate`} label={"Start Date"}>
                    <Popover2
                        placement="bottom-end"
                        interactionKind="click"
                        content={
                            <DatePicker
                                className={Classes.ELEVATION_1}
                                minDate={currentTime}
                                defaultValue={startDate}
                                onChange={(d) => {
                                    if (d) {
                                        updateValue("startDate", d);
                                    }
                                }}
                            />
                        }
                    >
                        <Button icon="calendar" text={shortDate(startDate)} rightIcon="caret-down" />
                    </Popover2>
                </FormGroup>

                <FormGroup key={`starttime`} label={"Start Time"}>
                    {/* //todo: figure out if it is a problem that users can create schedules that start before current time. */}
                    <TimePicker
                        useAmPm
                        minTime={currentTime}
                        defaultValue={getTime(startTime)}
                        onChange={(d) => updateValue("startTime", getFormattedTime(d))}
                    />
                </FormGroup>
            </div>

            <div style={{ display: "flex", gap: "2rem" }}>
                {scheduleType !== ScheduleFrequency.Periodically && (
                    <FormGroup key={`scheduletime`} label={"Schedule Time"}>
                        <TimePicker
                            useAmPm
                            defaultValue={getTime(scheduleTime)}
                            onChange={(d) => handleScheduleTimeChange(d)}
                        />
                    </FormGroup>
                )}
                <FormGroup key={`timezone`} label={"Time Zone"}>
                    <TimezonePicker
                        value={timeZone}
                        onChange={(t) => updateValue("timeZone", t)}
                        valueDisplayFormat={TimezoneDisplayFormat.COMPOSITE}
                        popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                        showLocalTimezone
                    />
                </FormGroup>
            </div>

            <RadioGroup
                inline
                label="Frequency"
                name="group"
                selectedValue={scheduleType}
                onChange={handleFrequencyChange}
            >
                {Object.values(ScheduleFrequency).map((freq, index) => (
                    <Radio key={index} label={freq} value={freq} />
                ))}
            </RadioGroup>

            {scheduleType === ScheduleFrequency.Weekly && <DayOfWeek days={daysOfWeek} update={updateValue} />}
            {scheduleType === ScheduleFrequency.Monthly && <DayOfMonth days={daysOfMonth} update={updateValue} />}
            {scheduleType === ScheduleFrequency.Periodically && <Periodic periodic={periodic} update={updateValue} />}
        </div>
    );
}

const addZero = (n: number) => ("0" + n).slice(-2);

const getFormattedTime = (d: Date) => {
    const hours = addZero(d.getHours());
    const minutes = addZero(d.getMinutes());
    return `${hours}:${minutes}`;
};

const getTime = (t: string) => {
    const [hour, minute] = t.split(":");
    const d = new Date();
    d.setHours(+hour, +minute);
    return d;
};
